import { useEffect, useRef, useState } from "react";
import { animate, createAnimatable, createScope, createTimeline, stagger } from "animejs";
import "./LoginMascots.css";

const MOTION_KEY = "epplehub_login_motion";
const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

function savedMotionPreference() {
  try {
    const saved = sessionStorage.getItem(MOTION_KEY);
    return saved === "on" ? true : saved === "off" ? false : null;
  } catch { return null; }
}

function Face({ x, y, tone }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <g className="epple-mascots__face">
        <g className="epple-mascots__eyes" fill="#fff">
          <ellipse cx="-6" cy="0" rx="3.3" ry="5" />
          <ellipse cx="6" cy="0" rx="3.3" ry="5" />
        </g>
        <g className="epple-mascots__lids" fill="none" stroke={tone} strokeWidth="2.3" strokeLinecap="round">
          <path d="M-9-1q3 3 6 0M3-1q3 3 6 0" />
        </g>
        <path className="epple-mascots__smile" d="M-3 9q3 3 6 0" fill="none" stroke={tone} strokeWidth="1.6" strokeLinecap="round" />
      </g>
    </g>
  );
}

/** All animation instances and input listeners belong to this mounted scene. */
export default function LoginMascots({ variant = "idle", period = "morning", energy = null, className = "" }) {
  const root = useRef(null);
  const scope = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [motionPreference, setMotionPreference] = useState(savedMotionPreference);
  const [salutation, setSalutation] = useState("");
  const mood = ["idle", "curious", "shy", "celebrate"].includes(variant) ? variant : "idle";
  const level = typeof energy === "number" && Number.isFinite(energy) ? clamp(energy, 0, 100) : 55;
  const motionEnabled = motionPreference ?? !reducedMotion;

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const change = () => setReducedMotion(media.matches);
    media.addEventListener("change", change);
    return () => media.removeEventListener("change", change);
  }, []);

  useEffect(() => {
    if (!motionEnabled) return undefined;
    const container = root.current;
    let pointerFrame = 0;
    let pointer = null;
    let visible = true;
    let observer;
    let gaze;
    const loops = [];
    const localScope = createScope({ root }).add(self => {
      const lift = mood === "shy" ? 7 : mood === "celebrate" ? 17 + level * .07 : 10 + level * .09;
      const pace = mood === "celebrate" ? .8 : 1.17 - level * .003;
      const idle = createTimeline({ loop: true, loopDelay: 190 * pace });
      idle.add(".epple-mascots__body", {
        y: [
          { to: 2, duration: 190 * pace, ease: "inQuad" },
          { to: -lift, duration: 460 * pace, ease: "outCubic" },
          { to: 0, duration: 610 * pace, ease: "inOutSine" },
        ],
        scaleX: [
          { to: 1.09, duration: 190 * pace },
          { to: .93, duration: 460 * pace },
          { to: 1.04, duration: 470 * pace },
          { to: 1, duration: 140 * pace },
        ],
        scaleY: [
          { to: .91, duration: 190 * pace },
          { to: 1.075, duration: 460 * pace },
          { to: .96, duration: 470 * pace },
          { to: 1, duration: 140 * pace },
        ],
        rotate: [{ to: -5, duration: 400 * pace }, { to: 5, duration: 440 * pace }, { to: 0, duration: 420 * pace }],
        delay: stagger(150), ease: "inOutSine",
      });
      loops.push(idle);
      loops.push(animate(".epple-mascots__eyes", {
        scaleY: [{ to: .08, duration: 85 }, { to: 1, duration: 110 }],
        delay: stagger(190, { start: 1600 }), loop: true, loopDelay: 1750, ease: "inOutSine",
      }));
      loops.push(animate(".epple-mascots__sparkle", {
        scale: [{ to: 1.2, duration: 700 }, { to: .8, duration: 700 }],
        opacity: [{ to: .95, duration: 700 }, { to: .35, duration: 700 }],
        rotate: [0, 24], alternate: true, loop: true, ease: "inOutSine",
      }));
      loops.push(animate(".epple-mascots__weather", { rotate: [-12, 12], duration: 1800, alternate: true, loop: true, ease: "inOutSine" }));
      gaze = createAnimatable(".epple-mascots__face", { x: 280, y: 280, ease: "outCubic" });
      if (mood === "curious") { gaze.x(3); gaze.y(3); }
      if (mood === "shy") {
        gaze.x(-2); gaze.y(-1);
        animate(".epple-mascots__hands", { y: [16, 0], opacity: [0, 1], duration: 430, ease: "outBack" });
      }
      // A separate transform group lets taps play without fighting the idle loop.
      const play = createTimeline({ autoplay: false });
      play.add(".epple-mascots__reaction", {
        y: [{ to: 3, duration: 110 }, { to: -(11 + level * .07), duration: 260, ease: "outCubic" }, { to: 0, duration: 430, ease: "outBounce" }],
        rotate: [{ to: -9, duration: 200 }, { to: 9, duration: 200 }, { to: 0, duration: 400 }],
        scaleX: [{ to: 1.12, duration: 110 }, { to: .92, duration: 260 }, { to: 1, duration: 430 }],
        scaleY: [{ to: .88, duration: 110 }, { to: 1.08, duration: 260 }, { to: 1, duration: 430 }],
        delay: stagger(85), ease: "outCubic",
      });
      self.add("play", () => play.restart());
    });
    scope.current = localScope;

    const followPointer = event => {
      if (mood === "shy" || !visible || event.pointerType === "touch") return;
      pointer = { x: event.clientX, y: event.clientY };
      if (pointerFrame) return;
      pointerFrame = requestAnimationFrame(() => {
        pointerFrame = 0;
        const bounds = container.getBoundingClientRect();
        gaze.x(clamp((pointer.x - bounds.left - bounds.width / 2) / 45, -5, 5));
        gaze.y(clamp((pointer.y - bounds.top - bounds.height / 2) / 50, -3.5, 4));
      });
    };
    const centerGaze = () => { if (mood !== "shy") { gaze.x(0); gaze.y(0); } };
    window.addEventListener("pointermove", followPointer, { passive: true });
    window.addEventListener("blur", centerGaze);
    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        loops.forEach(animation => visible ? animation.resume() : animation.pause());
      });
      observer.observe(container);
    }
    return () => {
      cancelAnimationFrame(pointerFrame);
      window.removeEventListener("pointermove", followPointer);
      window.removeEventListener("blur", centerGaze);
      observer?.disconnect();
      localScope.revert();
      if (scope.current === localScope) scope.current = null;
    };
  }, [mood, level, motionEnabled]);

  const toggleMotion = () => {
    const next = !motionEnabled;
    setMotionPreference(next);
    try { sessionStorage.setItem(MOTION_KEY, next ? "on" : "off"); } catch { /* Private browsing can disable storage. */ }
  };
  const play = () => {
    scope.current?.methods.play();
    setSalutation(motionEnabled ? "Hai, senang ketemu kamu!" : "Hai! Aktifkan animasi untuk bermain bersama.");
  };
  return (
    <div ref={root} className={`epple-mascots epple-mascots--${mood} ${className}`} data-testid="login-mascots" data-motion={motionEnabled ? "on" : "off"} data-energy={level}>
      <button type="button" className="epple-mascots__play" onClick={play} aria-label="Sapa karakter" data-testid="login-mascots-play" title="Klik atau sentuh untuk menyapa">
      <svg viewBox="-5 -28 240 158" focusable="false" aria-hidden="true">
        <ellipse cx="114" cy="116" rx="76" ry="7" fill="#000" opacity=".14" />
        <g className="epple-mascots__weather">
          {period === "night" ? <path d="M191 10a9 9 0 1 0 12 12 10 10 0 0 1-12-12" fill="#ccc9ff" /> : <g fill="#ffe1a2"><circle cx="194" cy="19" r="5" /><path d="M193 8h2v4h-2zm0 18h2v4h-2zm-10-8h4v2h-4zm18 0h4v2h-4z" /></g>}
        </g>
        <path className="epple-mascots__sparkle" d="m41 16 2.2 5.8 5.8 2.2-5.8 2.2L41 32l-2.2-5.8L33 24l5.8-2.2Z" fill="#d7deff" opacity=".7" />
        <circle cx="214" cy="85" r="2" fill="#aaa5ca" opacity=".6" />
        <g className="epple-mascots__body epple-mascots__body--coral">
          <g className="epple-mascots__reaction">
          <path d="M40 52c8-7 18-8 27-2 9 6 14 18 12 29-2 13-14 24-26 25l-4 8c-1 3-5 3-7 0l-4-8c-11-3-19-13-18-25 1-12 9-21 20-27Z" fill="#ff6a5f" />
          <ellipse cx="69" cy="44" rx="5" ry="7" transform="rotate(22 69 44)" fill="#ff6a5f" />
          <Face x={49} y={76} tone="#783b36" />
          </g>
        </g>
        <g className="epple-mascots__body epple-mascots__body--blue">
          <g className="epple-mascots__reaction">
          <g fill="#4b7bff">
            <circle cx="112" cy="57" r="30" />
            <circle cx="112" cy="28" r="13" /><circle cx="133" cy="35" r="13" />
            <circle cx="142" cy="56" r="13" /><circle cx="133" cy="78" r="13" />
            <circle cx="111" cy="86" r="13" /><circle cx="90" cy="78" r="13" />
            <circle cx="82" cy="56" r="13" /><circle cx="90" cy="35" r="13" />
          </g>
          <Face x={112} y={55} tone="#223c80" />
          <g className="epple-mascots__hands" fill="#7197ff">
            <ellipse cx="105" cy="56" rx="8" ry="10" transform="rotate(-24 105 56)" />
            <ellipse cx="120" cy="56" rx="8" ry="10" transform="rotate(24 120 56)" />
          </g>
          </g>
        </g>
        <g className="epple-mascots__body epple-mascots__body--amber">
          <g className="epple-mascots__reaction">
          <g fill="#ffb42e">
            <circle cx="177" cy="78" r="21" /><circle cx="176" cy="58" r="9" />
            <circle cx="195" cy="69" r="10" /><circle cx="194" cy="88" r="10" />
            <circle cx="177" cy="99" r="10" /><circle cx="159" cy="89" r="10" />
            <circle cx="159" cy="70" r="10" />
          </g>
          <Face x={177} y={77} tone="#79551f" />
          </g>
        </g>
      </svg>
      </button>
      <div className="epple-mascots__controls">
        <span>Sentuh kami</span><span aria-hidden="true">·</span>
        <button type="button" onClick={toggleMotion} aria-pressed={motionEnabled} data-testid="login-motion-toggle">{motionEnabled ? "Jeda animasi" : "Aktifkan animasi"}</button>
      </div>
      <span className="epple-mascots__sr" role="status">{salutation}</span>
    </div>
  );
}
